/**
 * space_sector.ts — the deterministic JTL-style sector layout (Approach A:
 * pure data, no DB rows). Every client derives the identical sector: the
 * Commons planet beneath the launch ring, sibling worlds, stations, belts,
 * a nebula, an ice field, a derelict hulk, a comet, a mining rig — and the
 * pirate dens that haunt the lanes between them. The server only mirrors
 * what it must (entrySlot — the launch ring — and the pirate bounty key
 * FORMAT; POI content is client-deterministic).
 *
 * SCALE (the 100× wave): sector coordinates run to ±2,400 km and planets
 * are tens of thousands of meters in radius — planetary, not prop-sized.
 * Local flight stays at JTL combat speeds; crossing the sector uses the
 * HYPERDRIVE (nav points below, N cycles / J jumps). SpaceModule keeps
 * render precision with a floating origin — logic runs in f64 absolute
 * coordinates; only offset-local positions reach the GPU.
 */

export const ENTRY_RADIUS_M = 2600; // MUST match server reducers/space.ts

/** Sector hard edge (absolute meters). */
export const SECTOR_EDGE_M = 2_400_000;

/** Hyperdrive cruise speed (m/s) — crossing the sector takes ~80 s. */
export const HYPER_SPEED = 30_000;

/** Launch/descent slot for an origin city (server mirror of entrySlot). */
export function entrySlot(originDbSid: number): { x: number; z: number } {
  const a = ((originDbSid * 137) % 360) * (Math.PI / 180);
  return { x: Math.cos(a) * ENTRY_RADIUS_M, z: Math.sin(a) * ENTRY_RADIUS_M };
}

export interface PlanetSpec {
  name: string;
  tex: string;          // asset-store key under textures/planets/ (4096×2048 albedo)
  normal: string;       // derived normal map (…_n.png) — '' = flat
  bump: number;         // normalScale (gas giants stay soft)
  radius: number;
  pos: [number, number, number];
  rim: number;          // atmosphere rim color
  spin: number;         // rad/s
}

export const PLANETS: PlanetSpec[] = [
  // The Commons itself — the ocean world you just left. It FILLS the nadir:
  // 78 km of planet whose limb wraps the whole lower sky from the ring.
  { name: 'The Commons', tex: 'textures/planets/planet_ocean.png', normal: 'textures/planets/planet_ocean_n.png', bump: 1.1, radius: 78_000, pos: [0, -84_500, 0], rim: 0x7ec8ff, spin: 0.004 },
  { name: 'Cindral', tex: 'textures/planets/planet_desert.png', normal: 'textures/planets/planet_desert_n.png', bump: 1.6, radius: 78_000, pos: [575_000, 130_000, -430_000], rim: 0xffb066, spin: 0.006 },
  { name: 'Vorune', tex: 'textures/planets/planet_gas.png', normal: 'textures/planets/planet_gas_n.png', bump: 0.45, radius: 240_000, pos: [-725_000, 260_000, 690_000], rim: 0xffd9a0, spin: 0.003 },
  // Palewater — a frozen ice-ocean world with its own 4K face.
  { name: 'Palewater', tex: 'textures/planets/planet_ice.png', normal: 'textures/planets/planet_ice_n.png', bump: 1.3, radius: 60_000, pos: [345_000, -78_000, 545_000], rim: 0xbfe8ff, spin: 0.005 },
  // Cindral's cratered moon — dense impact texture, the bumpiest body here.
  { name: 'Ashfall', tex: 'textures/planets/planet_moon.png', normal: 'textures/planets/planet_moon_n.png', bump: 2.0, radius: 14_000, pos: [500_000, 100_000, -355_000], rim: 0x998877, spin: 0.008 },
];

export type PoiKind =
  | 'station' | 'megastation' | 'asteroids' | 'junkyard'
  | 'nebula' | 'ice' | 'derelict' | 'comet' | 'mine' | 'fortress';

export interface PoiSpec {
  key: string;          // pirate bounty keys derive from this ("poi1:pirate0")
  name: string;
  kind: PoiKind;
  pos: [number, number, number];
  radius: number;
  pirates: number;      // deterministic FULL-AI pirate slots at this POI
  /** Pirate difficulty tier (JTL-style): 1 raiders, 2 corsairs, 3 reavers. */
  tier: 1 | 2 | 3;
  /** Instanced PATROL wings haunting the POI's volume (the ×50 population —
   *  cheap orbiting hulls; the nearest promote to full AI as you approach). */
  patrol: number;
}

export const POIS: PoiSpec[] = [
  // The original four keep their keys (bounty history + harness continuity).
  // Station radii carry the ×2 wave (structures render twice as tall too).
  { key: 'poi0', name: 'Waystation Aurel', kind: 'station', pos: [380_000, 26_000, 140_000], radius: 2_600, pirates: 0, tier: 1, patrol: 20 },
  { key: 'poi1', name: 'The Shard Belt', kind: 'asteroids', pos: [-320_000, -26_000, 430_000], radius: 7_000, pirates: 3, tier: 1, patrol: 85 },
  { key: 'poi2', name: "Reaver's Junkyard", kind: 'junkyard', pos: [540_000, -42_000, -490_000], radius: 5_200, pirates: 4, tier: 2, patrol: 70 },
  { key: 'poi3', name: 'The Long Dark', kind: 'asteroids', pos: [-620_000, 90_000, -360_000], radius: 8_500, pirates: 3, tier: 2, patrol: 85 },
  // The 100× wave: deeper space, stranger places.
  { key: 'poi4', name: 'Nova Aurel Station', kind: 'station', pos: [1_050_000, 240_000, -780_000], radius: 2_900, pirates: 0, tier: 1, patrol: 20 },
  { key: 'poi5', name: 'The Veil Nebula', kind: 'nebula', pos: [-420_000, 60_000, -520_000], radius: 30_000, pirates: 2, tier: 2, patrol: 60 },
  { key: 'poi6', name: 'Palewater Ice Field', kind: 'ice', pos: [560_000, -120_000, 900_000], radius: 12_000, pirates: 2, tier: 2, patrol: 60 },
  { key: 'poi7', name: 'The Silent Hulk', kind: 'derelict', pos: [-700_000, 180_000, 300_000], radius: 3_000, pirates: 3, tier: 2, patrol: 55 },
  { key: 'poi8', name: 'Kessel Run Scar', kind: 'asteroids', pos: [300_000, -40_000, -350_000], radius: 15_000, pirates: 3, tier: 2, patrol: 90 },
  { key: 'poi9', name: 'Redhand Fortress', kind: 'fortress', pos: [-1_050_000, 320_000, 850_000], radius: 6_000, pirates: 6, tier: 3, patrol: 90 },
  { key: 'poi10', name: "Drifter's Mine", kind: 'mine', pos: [820_000, 90_000, 120_000], radius: 4_000, pirates: 1, tier: 1, patrol: 30 },
  { key: 'poi11', name: 'Wanderer Comet', kind: 'comet', pos: [150_000, 420_000, 640_000], radius: 2_500, pirates: 0, tier: 1, patrol: 20 },
  // ── The HOME CLUSTER: orbit-of-the-Commons life, minutes from the ring ──
  // Aurel Prime is the MASSIVE anchor station — visible from launch, docks,
  // repairs, and the hub the freighter lanes breathe through.
  { key: 'poi12', name: 'Aurel Prime', kind: 'megastation', pos: [9_500, 900, 5_200], radius: 5_200, pirates: 0, tier: 1, patrol: 25 },
  { key: 'poi13', name: 'Homefield Belt', kind: 'asteroids', pos: [-14_000, -700, 11_500], radius: 3_200, pirates: 2, tier: 1, patrol: 45 },
  { key: 'poi14', name: 'Orbit Yard', kind: 'junkyard', pos: [18_500, -1_400, -13_000], radius: 2_400, pirates: 2, tier: 1, patrol: 40 },
  { key: 'poi15', name: 'Beacon Verge', kind: 'nebula', pos: [-9_500, 1_800, -12_500], radius: 4_500, pirates: 1, tier: 1, patrol: 30 },
  // ── The FILLED-SKY wave (2026-08-07): space stops being empty ────────────
  { key: 'poi16', name: 'The Ashen Reach', kind: 'asteroids', pos: [-150_000, 40_000, 1_150_000], radius: 18_000, pirates: 3, tier: 2, patrol: 90 },
  { key: 'poi17', name: 'Coldline Shoals', kind: 'asteroids', pos: [950_000, -180_000, 450_000], radius: 13_000, pirates: 3, tier: 2, patrol: 90 },
  { key: 'poi18', name: "The Widow's Teeth", kind: 'asteroids', pos: [-1_350_000, -60_000, -150_000], radius: 21_000, pirates: 3, tier: 3, patrol: 95 },
  { key: 'poi19', name: 'Emberfall Drift', kind: 'asteroids', pos: [200_000, 260_000, 1_500_000], radius: 16_000, pirates: 3, tier: 2, patrol: 90 },
  { key: 'poi20', name: 'The Graveyard Ring', kind: 'asteroids', pos: [1_400_000, 60_000, 300_000], radius: 24_000, pirates: 3, tier: 3, patrol: 90 },
  { key: 'poi21', name: 'Farlight Terminal', kind: 'station', pos: [-800_000, -140_000, -900_000], radius: 2_700, pirates: 0, tier: 1, patrol: 15 },
  { key: 'poi22', name: 'Kessway Depot', kind: 'station', pos: [520_000, 180_000, 1_250_000], radius: 2_400, pirates: 0, tier: 1, patrol: 15 },
  { key: 'poi23', name: 'The Murk', kind: 'nebula', pos: [880_000, -60_000, -1_150_000], radius: 26_000, pirates: 2, tier: 2, patrol: 45 },
  { key: 'poi24', name: 'Shiverfield', kind: 'ice', pos: [-350_000, -240_000, 1_400_000], radius: 14_000, pirates: 2, tier: 2, patrol: 45 },
  { key: 'poi25', name: "Breaker's Scrapline", kind: 'junkyard', pos: [-1_500_000, 220_000, 500_000], radius: 7_000, pirates: 3, tier: 2, patrol: 60 },
  { key: 'poi26', name: 'The Drowned Carrier', kind: 'derelict', pos: [1_200_000, 300_000, -300_000], radius: 3_500, pirates: 3, tier: 2, patrol: 50 },
  { key: 'poi27', name: 'Blackharrow Bastion', kind: 'fortress', pos: [700_000, -320_000, -1_350_000], radius: 7_000, pirates: 6, tier: 3, patrol: 95 },
  { key: 'poi28', name: 'Sable Claim', kind: 'mine', pos: [-550_000, 340_000, -1_100_000], radius: 4_500, pirates: 1, tier: 1, patrol: 25 },
  { key: 'poi29', name: 'Pilgrim Comet', kind: 'comet', pos: [-1_150_000, -420_000, -700_000], radius: 2_800, pirates: 0, tier: 1, patrol: 12 },
];

/** Total enemy-ship population (full-AI slots + instanced patrols) — the ×50
 *  wave; the __space diagnostic reports it. */
export const TOTAL_ENEMIES = POIS.reduce((n, p) => n + p.pirates + p.patrol, 0);

/**
 * Freighter lanes — ambient carrier traffic that warps in, cruises the lane,
 * and warps out (the planet's breath). Deterministic schedule: one carrier
 * per lane, staggered; positions are ABSOLUTE sector meters.
 */
export interface FreightLane { key: string; from: [number, number, number]; to: [number, number, number]; periodS: number }
export const FREIGHT_LANES: FreightLane[] = [
  { key: 'lane0', from: [9_500, 900, 5_200], to: [-14_000, -700, 11_500], periodS: 38 },   // Aurel Prime ↔ Homefield Belt
  { key: 'lane1', from: [26_000, 3_000, -9_000], to: [9_500, 900, 5_200], periodS: 47 },   // deep-space arrival → Aurel Prime
  { key: 'lane2', from: [9_500, 900, 5_200], to: [18_500, -1_400, -13_000], periodS: 55 }, // Aurel Prime ↔ Orbit Yard
  { key: 'lane3', from: [-22_000, 2_400, -2_000], to: [9_500, 900, 5_200], periodS: 63 },  // trade arrival → Aurel Prime
];

/**
 * Pirate tier stats (SWG-style ladder: T1 commerce raiders → T3 den reavers).
 * speed m/s · fire cooldown s · lead 0..1 (how well their bolts lead you —
 * the FreeSpace difficulty-accuracy knob) · label for target names.
 */
export const TIER_STATS: Record<1 | 2 | 3, { hp: number; speed: number; fireCd: number; lead: number; label: string }> = {
  1: { hp: 3, speed: 62, fireCd: 1.05, lead: 0.35, label: 'raider' },
  2: { hp: 4, speed: 78, fireCd: 0.85, lead: 0.65, label: 'corsair' },
  3: { hp: 5, speed: 92, fireCd: 0.68, lead: 0.85, label: 'reaver' },
};

/** Nav targets the hyperdrive cycles through (N): POIs, then planets. */
export interface NavPoint { name: string; pos: [number, number, number]; arrive: number }
export function navPoints(): NavPoint[] {
  const out: NavPoint[] = POIS.map((p) => ({ name: p.name, pos: p.pos, arrive: Math.max(9_000, p.radius * 1.6) }));
  for (const pl of PLANETS) {
    if (pl.name === 'The Commons') continue; // you LEFT it; the beacon lands you
    out.push({ name: pl.name, pos: pl.pos, arrive: pl.radius * 2.2 });
  }
  return out;
}

// ─── PUBLIC user worlds as PLANETS (the galactic-map wave) ───────────────────
// MIRROR of server/src/lib/space_worlds.ts — the map and the server must
// agree where a planet hangs (the resource_catalog mirror law).

function wMix(h: number): number {
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
function wFnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
const h01w = (wid: number, salt: string): number => wMix(wFnv(`${wid}|${salt}`)) / 0xffffffff;

export const WORLD_BAND_MIN_M = 380_000;
export const WORLD_BAND_MAX_M = 1_930_000;
export const WORLD_LAND_MULT = 2.6;
export const WORLD_WARP_MULT = 2.0;

export interface WorldPlanet {
  x: number; y: number; z: number;
  radius: number;
  texIdx: number;
  hue: number;
  spin: number;
}

export function worldPlanetOf(worldId: bigint): WorldPlanet {
  const wid = Number(worldId % 16_777_216n);
  const a = h01w(wid, 'ang') * Math.PI * 2;
  const r = WORLD_BAND_MIN_M + h01w(wid, 'rad') * (WORLD_BAND_MAX_M - WORLD_BAND_MIN_M);
  return {
    x: Math.cos(a) * r,
    y: (h01w(wid, 'inc') - 0.5) * 360_000,
    z: Math.sin(a) * r,
    radius: 18_000 + h01w(wid, 'size') * 26_000,
    texIdx: Math.floor(h01w(wid, 'tex') * 5) % 5,
    hue: h01w(wid, 'hue'),
    spin: 0.003 + h01w(wid, 'spin') * 0.006,
  };
}

export function worldPlanetEligible(kind: string, visibility: string, quarantined: boolean): boolean {
  // 'merchant' — the platform's Merchant Hive hangs as a landable commerce planet.
  return (kind === 'group' || kind === 'individual' || kind === 'merchant') && visibility === 'public' && !quarantined;
}

export function worldWarpArrival(worldId: bigint): { x: number; y: number; z: number } {
  const pl = worldPlanetOf(worldId);
  const d = Math.hypot(pl.x, pl.y, pl.z) || 1;
  const m = pl.radius * WORLD_WARP_MULT;
  return { x: pl.x - (pl.x / d) * m, y: pl.y - (pl.y / d) * m, z: pl.z - (pl.z / d) * m };
}

/** Face pool for user-world planets (texIdx indexes this). */
export const WORLD_PLANET_POOL: Array<{ tex: string; normal: string; bump: number }> = [
  { tex: 'textures/planets/planet_desert.png', normal: 'textures/planets/planet_desert_n.png', bump: 1.6 },
  { tex: 'textures/planets/planet_gas.png', normal: 'textures/planets/planet_gas_n.png', bump: 0.45 },
  { tex: 'textures/planets/planet_ice.png', normal: 'textures/planets/planet_ice_n.png', bump: 1.3 },
  { tex: 'textures/planets/planet_moon.png', normal: 'textures/planets/planet_moon_n.png', bump: 2.0 },
  { tex: 'textures/planets/planet_ocean.png', normal: 'textures/planets/planet_ocean_n.png', bump: 1.1 },
];

export const PIRATE_RESPAWN_S = 600; // MUST match server PIRATE_RESPAWN_S

function h01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

/**
 * Deterministic PATROL orbit for a POI's instanced wing ship: each hull flies
 * a slow tilted circle somewhere in the POI's greater volume (up to ~3× the
 * den radius — patrols RANGE, which is what fills the sky between landmarks).
 * Position at shared time t: center + R·(cos θ, sin θ·tilt, sin θ) rotated by
 * the orbit's own basis; θ = phase + ω·t.
 */
export interface PatrolOrbit {
  cx: number; cy: number; cz: number;   // orbit center (absolute)
  r: number;                            // orbit radius
  omega: number;                        // rad/s
  phase: number;
  tilt: number;                         // out-of-plane lean 0..0.6
  basis: number;                        // orbit-plane rotation about Y
  size: number;                         // hull scale (m along the long axis)
}
export function patrolOrbit(poi: PoiSpec, idx: number): PatrolOrbit {
  const a = h01(idx, `${poi.key}pa`) * Math.PI * 2;
  const d = poi.radius * (0.4 + Math.pow(h01(idx, `${poi.key}pd`), 0.7) * 2.6);
  const y = (h01(idx, `${poi.key}py`) - 0.5) * poi.radius * 1.6;
  return {
    cx: poi.pos[0] + Math.cos(a) * d,
    cy: poi.pos[1] + y,
    cz: poi.pos[2] + Math.sin(a) * d,
    r: 120 + h01(idx, `${poi.key}pr`) * 900,
    omega: (0.008 + h01(idx, `${poi.key}pw`) * 0.03) * (h01(idx, `${poi.key}ps`) < 0.5 ? -1 : 1),
    phase: h01(idx, `${poi.key}pp`) * Math.PI * 2,
    tilt: h01(idx, `${poi.key}pt`) * 0.6,
    basis: h01(idx, `${poi.key}pb`) * Math.PI * 2,
    size: 10 + h01(idx, `${poi.key}pz`) * 6,
  };
}

/**
 * CARGO CONVOYS — big freighters hauling the long lanes between stations,
 * escorted by faction fighters, AMBUSHED by pirate raiders mid-route (the
 * living skirmish the sector tells stories about). Shared wall clock: every
 * client watches the same convoy at the same lane position.
 */
export interface ConvoyRoute {
  key: string;
  from: [number, number, number];
  to: [number, number, number];
  periodS: number;      // full schedule period (flight + gap)
  escorts: number;      // faction fighters flying cover
  raiders: number;      // pirate attackers during the ambush window
}
export const CONVOY_ROUTES: ConvoyRoute[] = [
  { key: 'cv0', from: [380_000, 26_000, 140_000], to: [1_050_000, 240_000, -780_000], periodS: 840, escorts: 3, raiders: 3 },  // Waystation Aurel → Nova Aurel
  { key: 'cv1', from: [9_500, 900, 5_200], to: [-800_000, -140_000, -900_000], periodS: 960, escorts: 3, raiders: 4 },                                   // Aurel Prime → Farlight Terminal
  { key: 'cv2', from: [520_000, 180_000, 1_250_000], to: [380_000, 26_000, 140_000], periodS: 900, escorts: 2, raiders: 3 },                             // Kessway Depot → Waystation Aurel
  { key: 'cv3', from: [-800_000, -140_000, -900_000], to: [9_500, 900, 5_200], periodS: 1_080, escorts: 3, raiders: 4 },                                 // Farlight Terminal → Aurel Prime
] as ConvoyRoute[];
export const CONVOY_SPEED = 320;        // m/s along the lane
/** Ambush window (fractions of the flight) — raiders warp in, harry the
 *  freighter, and the escorts drive them off. */
export const CONVOY_AMBUSH = { start: 0.35, end: 0.7 };

/** Deterministic pirate spawn offsets inside a POI. */
export function pirateSpawn(poi: PoiSpec, idx: number): [number, number, number] {
  const a = h01(idx, `${poi.key}a`) * Math.PI * 2;
  const r = poi.radius * (0.35 + h01(idx, `${poi.key}r`) * 0.55);
  const y = (h01(idx, `${poi.key}y`) - 0.5) * poi.radius * 0.5;
  return [poi.pos[0] + Math.cos(a) * r, poi.pos[1] + y, poi.pos[2] + Math.sin(a) * r];
}

/** Deterministic asteroid placements for a field POI. */
export function asteroidField(poi: PoiSpec, count = 42): Array<{ pos: [number, number, number]; scale: number; spin: number }> {
  const out: Array<{ pos: [number, number, number]; scale: number; spin: number }> = [];
  for (let i = 0; i < count; i++) {
    const a = h01(i, `${poi.key}fa`) * Math.PI * 2;
    const r = poi.radius * Math.sqrt(h01(i, `${poi.key}fr`));
    const y = (h01(i, `${poi.key}fy`) - 0.5) * poi.radius * 0.6;
    out.push({
      pos: [poi.pos[0] + Math.cos(a) * r, poi.pos[1] + y, poi.pos[2] + Math.sin(a) * r],
      // Field rocks scale with the field — sector-scale belts read as BELTS.
      scale: (3 + h01(i, `${poi.key}fs`) * 14) * Math.max(1, poi.radius / 900),
      spin: (h01(i, `${poi.key}fw`) - 0.5) * 0.4,
    });
  }
  return out;
}

/** Deterministic nebula puff placements (big additive sprites). */
export function nebulaPuffs(poi: PoiSpec, count = 16): Array<{ pos: [number, number, number]; scale: number; hue: number }> {
  const out: Array<{ pos: [number, number, number]; scale: number; hue: number }> = [];
  for (let i = 0; i < count; i++) {
    const a = h01(i, `${poi.key}na`) * Math.PI * 2;
    const r = poi.radius * Math.pow(h01(i, `${poi.key}nr`), 0.6);
    const y = (h01(i, `${poi.key}ny`) - 0.5) * poi.radius * 0.7;
    out.push({
      pos: [poi.pos[0] + Math.cos(a) * r, poi.pos[1] + y, poi.pos[2] + Math.sin(a) * r],
      scale: poi.radius * (0.5 + h01(i, `${poi.key}ns`) * 0.7),
      hue: h01(i, `${poi.key}nh`),
    });
  }
  return out;
}

// ─── Faction wings: the two versona civs FLY their space ────────────────────
// Deterministic ping-pong lanes between POIs on the shared wall clock —
// every client sees the same wings in the same places. Your civ's wings are
// ALLIES (they escort and return fire); the opposing civ's wings mark you.
export interface WingSpec {
  key: string;
  civ: 'aurelian' | 'borean';
  ships: number;
  a: readonly number[];
  b: readonly number[];
  period: number;   // s for a full there-and-back
  phase: number;    // 0..1 lane offset
}

const WING_SPEED = 420; // m/s cruise
function laneOf(fromKey: string, toKey: string): { a: readonly number[]; b: readonly number[]; period: number } {
  const pa = POIS.find((p) => p.key === fromKey)!;
  const pb = POIS.find((p) => p.key === toKey)!;
  const len = Math.hypot(pb.pos[0] - pa.pos[0], pb.pos[1] - pa.pos[1], pb.pos[2] - pa.pos[2]);
  return { a: pa.pos, b: pb.pos, period: Math.max(600, (len * 2) / WING_SPEED) };
}

export const FACTION_WINGS: WingSpec[] = [
  { key: 'wg0', civ: 'aurelian', ships: 3, phase: 0.00, ...laneOf('poi12', 'poi0') },
  { key: 'wg1', civ: 'aurelian', ships: 4, phase: 0.35, ...laneOf('poi0', 'poi4') },
  { key: 'wg2', civ: 'aurelian', ships: 3, phase: 0.60, ...laneOf('poi12', 'poi22') },
  { key: 'wg3', civ: 'aurelian', ships: 3, phase: 0.15, ...laneOf('poi4', 'poi21') },
  { key: 'wg4', civ: 'borean', ships: 3, phase: 0.10, ...laneOf('poi21', 'poi12') },
  { key: 'wg5', civ: 'borean', ships: 4, phase: 0.45, ...laneOf('poi22', 'poi27') },
  { key: 'wg6', civ: 'borean', ships: 3, phase: 0.72, ...laneOf('poi21', 'poi9') },
  { key: 'wg7', civ: 'borean', ships: 3, phase: 0.28, ...laneOf('poi22', 'poi0') },
];

/** Wing lead position + travel direction at wall-clock t (triangle ping-pong). */
export function wingPos(w: WingSpec, tSec: number): { x: number; y: number; z: number; dx: number; dy: number; dz: number } {
  const c = (tSec / w.period + w.phase) % 1;
  const u = c < 0.5 ? c * 2 : 2 - c * 2;          // 0→1→0
  const sign = c < 0.5 ? 1 : -1;
  const x = w.a[0] + (w.b[0] - w.a[0]) * u;
  const y = w.a[1] + (w.b[1] - w.a[1]) * u + Math.sin(tSec * 0.02 + w.phase * 9) * 400;
  const z = w.a[2] + (w.b[2] - w.a[2]) * u;
  const len = Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1], w.b[2] - w.a[2]) || 1;
  return { x, y, z, dx: ((w.b[0] - w.a[0]) / len) * sign, dy: ((w.b[1] - w.a[1]) / len) * sign, dz: ((w.b[2] - w.a[2]) / len) * sign };
}

/** Void-raider cells: the empty space BETWEEN dens hashes to sparse ambushers. */
export const VOID_CELL_M = 22_000;
export const VOID_RAIDER_P = 0.3;
